/* EM Engraving for Broadcast (with external mount support) */
(() => {
  'use strict';

  const SECTION_ATTR = 'data-engraving-root';
  const EVENTS = {
    APPLIED: 'engraving:applied',
    CLEARED: 'engraving:cleared',
    OPENED: 'engraving:opened',
    CLOSED: 'engraving:closed'
  };

  // ===== Page scroll lock (kept for dialog) =====
  const HTML = document.documentElement;
  const BODY = document.body;
  const LOCK_CLASS = 'em-engr-lock';
  let __emScrollY = 0;
  function lockScroll(){ if(BODY.classList.contains(LOCK_CLASS))return;
    __emScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    HTML.classList.add(LOCK_CLASS); BODY.classList.add(LOCK_CLASS);
    BODY.style.position='fixed'; BODY.style.top=`-${__emScrollY}px`;
    BODY.style.left='0'; BODY.style.right='0'; BODY.style.width='100%';
  }
  function unlockScroll(){ if(!BODY.classList.contains(LOCK_CLASS))return;
    HTML.classList.remove(LOCK_CLASS); BODY.classList.remove(LOCK_CLASS);
    BODY.style.position=''; BODY.style.top=''; BODY.style.left='';
    BODY.style.right=''; BODY.style.width=''; window.scrollTo(0,__emScrollY||0);
  }

  const qsa = (r, sel) => Array.from(r.querySelectorAll(sel));
  const create = (tag, props = {}, children = []) => {
    const el = document.createElement(tag);
    Object.entries(props).forEach(([k, v]) => {
      if (k === 'class') el.className = v;
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
      else if (v !== undefined && v !== null) el.setAttribute(k, v);
    });
    children.forEach(c => el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c));
    return el;
  };

  const injectFontFace = (family, weight, url) => {
    if (!url) return;
    const id = `em-font-${btoa(`${family}-${weight}-${url}`).replace(/=+/g,'')}`;
    if (document.getElementById(id)) return;
    const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
    const fmt = ext === 'woff2' ? 'woff2' : (ext === 'woff' ? 'woff' : (ext === 'ttf' ? 'truetype' : 'opentype'));
    const css = `@font-face{font-family:'${family}';src:url('${url}') format('${fmt}');font-weight:${weight||'400'};font-style:normal;font-display:swap}`;
    const style = create('style', { id });
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  };

  const FocusTrap = (dialog) => {
    let focusables = [];
    const sel = 'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])';
    const update = () => focusables = qsa(dialog, sel).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    const onKey = (e) => {
      if (e.key === 'Tab') {
        update();
        if (!focusables.length) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      } else if (e.key === 'Escape') dialog.dispatchEvent(new CustomEvent('em:escape'));
    };
    return { activate(){ dialog.addEventListener('keydown', onKey); update(); },
             deactivate(){ dialog.removeEventListener('keydown', onKey); } };
  };

  // (Kept for completeness; not used in this flow)
  function openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: 'cart' } }));
    const toggles = document.querySelectorAll(
      '[data-cart-drawer-toggle],[data-cart-toggle],[data-open-cart],[aria-controls="CartDrawer"],a[href="#CartDrawer"],button[name="open-cart"]'
    );
    for (const t of toggles) { try { t.click(); return; } catch(_){} }
    try { window.theme?.cart?.open?.(); } catch(_){}
  }

  class Engraving {
    constructor(root) {
      this.root = root;
      try { this.cfg = JSON.parse(root.getAttribute('data-config') || '{}'); }
      catch { this.cfg = {}; }
      this.cfg.textColor = this.cfg.textColor || '#8F5F25';

      this.dialog = root.querySelector('dialog.em-engr__drawer');
      this.trigger = root.querySelector('[data-engraving-trigger]');
      this.triggerLabel = root.querySelector('[data-engraving-trigger-label]');
      this.formWrap = root.querySelector('[data-engraving-fields]');
      this.linesWrap = root.querySelector('[data-engraving-lines]');
      this.fontList = root.querySelector('[data-engraving-fonts]');
      this.fontLabel = root.querySelector('[data-engraving-fontlabel]');
      this.svgText = root.querySelector('[data-engraving-svg-text]');
      this.ariaLive = root.querySelector('[data-engraving-aria-live]');
      this.clearBtn = root.querySelector('[data-engraving-clear]');
      this.applyBtn = root.querySelector('[data-engraving-apply]');
      this.closeBtn = root.querySelector('[data-engraving-close]');

      this.cfg.maxLines = Number(this.cfg.maxLines || 1);
      this.cfg.maxCharsPerLine = Number(this.cfg.maxCharsPerLine || 12);
      this.cfg.allowedCharset = this.cfg.allowedCharset || '^[A-Za-zА-Яа-Я0-9\\s&.\\-]+$';
      this.cfg.fontSizePx = Number(this.cfg.fontSizePx || 28);
      this.cfg.centerY = Number(this.cfg.centerY || 520);
      this.cfg.productFormSelector = this.cfg.productFormSelector || 'form[action="/cart/add"]';
      this.cfg.mountSelector = this.cfg.mountSelector || '#em-engr-mount';
      this.cfg.i18n = this.cfg.i18n || {};

      this.inputs = [];
      this.currentFont = null;
      this.focusTrap = FocusTrap(this.dialog);

      // Persist last Apply payload in case the form is re-rendered
      this.savedProps = null;

      this._maybeMountTrigger();
      this._bind();
      this._buildLines();
      this._buildFonts();
      this._updatePreview();

      // Guards to guarantee properties are present when the product is added
      this._installSubmitGuard();
      this._installClickGuard();      // <— targets the exact clicked form
      this._installFormObserver();
    }

    _maybeMountTrigger() {
      try {
        const mount = document.querySelector(this.cfg.mountSelector);
        if (mount && this.trigger) { mount.innerHTML = ''; mount.appendChild(this.trigger); }
      } catch (_) {}
    }

    _bind() {
      if (this.trigger) this.trigger.addEventListener('click', () => this.open());
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clear());
      if (this.applyBtn) this.applyBtn.addEventListener('click', () => this.apply());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('em:escape', () => this.close());
      this.dialog?.addEventListener('close', () => unlockScroll());
      this.dialog?.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });

      // Font tile -> preview
      this.root.addEventListener('engraving:fontchange', (ev) => {
        const f = ev.detail; if (!f) return;
        this.currentFont = f;
        if (this.fontLabel) {
          const label = f.label || f.family || '';
          this.fontLabel.textContent = (this.cfg.i18n.fontLabel || 'Шрифт: {{font}}').replace('{{font}}', label);
        }
        this._updatePreview();
      });

      // Re-install guards & re-inject after re-renders / variant changes
      document.addEventListener('shopify:section:load', () => {
        this._installClickGuard(true);
        this._installSubmitGuard(true);
        const f = this._productForm();
        if (f && this.savedProps) this._writeHiddenPropsToForm(f, this.savedProps);
      });
      document.addEventListener('variant:change', () => {
        const f = this._productForm();
        if (f && this.savedProps) this._writeHiddenPropsToForm(f, this.savedProps);
      });
    }

    open() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.removeAttribute('hidden');
      lockScroll();
      this.focusTrap.activate();
      this.dialog.dispatchEvent(new Event(EVENTS.OPENED, { bubbles: true }));
      const firstInput = this.linesWrap.querySelector('input');
      if (firstInput) setTimeout(() => firstInput.focus(), 10);
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.setAttribute('hidden', '');
      unlockScroll();
      this.focusTrap.deactivate();
      this.dialog.dispatchEvent(new Event(EVENTS.CLOSED, { bubbles: true }));
      this.trigger?.focus();
    }

    _buildLines() {
      this.linesWrap.innerHTML = '';
      this.inputs = [];
      const re = new RegExp(this.cfg.allowedCharset);
      for (let i = 1; i <= this.cfg.maxLines; i++) {
        const id = `em-engr-line-${i}-${Math.random().toString(36).slice(2,7)}`;
        const label = create('label', { class: 'em-engr__label', for: id }, [
          (this.cfg.i18n.line || 'Ред {{count}}:').replace('{{count}}', i)
        ]);
        const counter = create('span', { class: 'em-engr__counter', 'aria-hidden': 'true' }, [
          (this.cfg.i18n.chars || '{{current}}/{{total}}').replace('{{current}}', '0').replace('{{total}}', String(this.cfg.maxCharsPerLine))
        ]);
        label.appendChild(counter);

        const input = create('input', {
          class: 'em-engr__input', id, maxlength: String(this.cfg.maxCharsPerLine),
          type: 'text', 'data-index': String(i)
        });
        const error = create('div', { class: 'em-engr__error', 'aria-live': 'polite' });

        input.addEventListener('input', () => {
          const val = input.value || '';
          counter.textContent = (this.cfg.i18n.chars || '{{current}}/{{total}}')
            .replace('{{current}}', String(val.length))
            .replace('{{total}}', String(this.cfg.maxCharsPerLine));
          if (val.length && !re.test(val)) {
            input.setAttribute('aria-invalid', 'true');
            error.textContent = this.cfg.i18n.errors?.disallowed || 'Невалиден символ.';
          } else {
            input.removeAttribute('aria-invalid');
            error.textContent = '';
          }
          this._updatePreview();
        });

        const wrap = create('div', { class: 'em-engr__line' }, [label, input, error]);
        this.linesWrap.appendChild(wrap);
        this.inputs.push({ input, error, counter });
      }
    }

    _buildFonts() {
      this.fontList.innerHTML = '';
      let fonts = [];
      try { fonts = Array.isArray(this.cfg.fonts) ? this.cfg.fonts : JSON.parse(this.cfg.fonts || '[]'); }
      catch { fonts = []; }
      if (fonts.length) {
        fonts.forEach(f => { if (f.file_url) injectFontFace(f.family || f.label, f.weight||'400', f.file_url); });
        this.currentFont = fonts[0];
      } else {
        this.currentFont = { label: 'Helvetica', family: 'Helvetica', weight: '400', fallback: 'Arial, system-ui, sans-serif' };
      }
      if (this.fontLabel) {
        const l = this.currentFont.label || this.currentFont.family || '';
        this.fontLabel.textContent = (this.cfg.i18n.fontLabel || 'Шрифт: {{font}}').replace('{{font}}', l);
      }
    }

    _updatePreview() {
      const values = this.inputs.map(o => (o.input.value || '').trim());
      while (this.svgText.firstChild) this.svgText.removeChild(this.svgText.firstChild);
      const lh = Math.round(this.cfg.fontSizePx * 1.2);
      const ys = (this.cfg.maxLines === 1)
        ? [this.cfg.centerY]
        : [this.cfg.centerY - Math.round(lh * 0.55), this.cfg.centerY + Math.round(lh * 0.55)];
      values.forEach((val, i) => {
        const t = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        t.setAttribute('x', '50%');
        t.setAttribute('y', String(ys[i] || this.cfg.centerY));
        t.textContent = val;
        this.svgText.appendChild(t);
      });
      const family = this.currentFont?.family || 'system-ui';
      const fallback = this.currentFont?.fallback || 'system-ui, Arial, sans-serif';
      const weight = this.currentFont?.weight || '400';
      this.svgText.setAttribute('style', `font-size:${this.cfg.fontSizePx}px;fill:${this.cfg.textColor};font-family:'${family}', ${fallback};font-weight:${weight}`);
    }

    _productForm() {
      let form = document.querySelector(this.cfg.productFormSelector);
      if (!form) {
        const candidates = qsa(document, 'form[action="/cart/add"]');
        form = candidates.find(f => f.querySelector('input[name="id"]')) || candidates[0] || null;
      }
      return form;
    }

    _writeHiddenPropsToForm(form, props) {
      if (!form) return;
      // Remove our previous fields in THIS form
      qsa(form,
        'input[name^="properties[Гравиране – "],' +
        'input[name="properties[_engraving_payload_json]"],' +
        'input[name="properties[_engraving_applied]"],' +
        'input[name="properties[Engraving – Font]"]'
      ).forEach(n => n.remove());

      // Add display properties
      Object.entries(props).forEach(([key, val]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = String(val);
        form.appendChild(input);
      });

      // Optional helpers
      const jsonInput = document.createElement('input');
      jsonInput.type = 'hidden';
      jsonInput.name = 'properties[_engraving_payload_json]';
      jsonInput.value = JSON.stringify(props);
      form.appendChild(jsonInput);

      const flagInput = document.createElement('input');
      flagInput.type = 'hidden';
      flagInput.name = 'properties[_engraving_applied]';
      flagInput.value = '1';
      form.appendChild(flagInput);
    }

    /* NEW: write to ALL /cart/add forms at once */
    _writeHiddenPropsToAllForms(props){
      const forms = document.querySelectorAll('form[action="/cart/add"]');
      forms.forEach(f => this._writeHiddenPropsToForm(f, props));
    }

    _installSubmitGuard(reinstall = false) {
      if (this._onSubmit && !reinstall) return;
      if (reinstall && this._onSubmit) {
        document.removeEventListener('submit', this._onSubmit, true);
      }
      this._onSubmit = (ev) => {
        const form = ev.target;
        if (!(form instanceof HTMLFormElement)) return;
        // Only forms that actually add to cart
        const isCartAdd = (form.getAttribute('action') || '').includes('/cart/add');
        if (!isCartAdd) return;

        const anyText = this.inputs.some(o => (o.input.value || '').trim().length > 0);
        const props = anyText ? this._collectEngravingProps() : this.savedProps;
        if (props && Object.keys(props).length) this._writeHiddenPropsToForm(form, props);
      };
      document.addEventListener('submit', this._onSubmit, true); // capture
    }

    _installClickGuard(reinstall = false) {
      // Capture clicks anywhere on the page; if the target is an add-to-cart button,
      // write props into the *nearest form being used*.
      if (this._onClick && !reinstall) return;
      if (reinstall && this._onClick) {
        document.removeEventListener('click', this._onClick, true);
      }

      this._onClick = (ev) => {
        const target = ev.target;
        if (!(target instanceof Element)) return;

        // A button or control that triggers add-to-cart
        const btn = target.closest(
          'button, input[type="submit"], [name="add"], .product-form__submit, button.add-to-cart, [data-add-to-cart]'
        );
        if (!btn) return;

        // Form *being used by this button* (sticky bar / modal / quick-add)
        const form = btn.closest('form');
        if (!form) return;
        const isCartAdd = (form.getAttribute('action') || '').includes('/cart/add');
        if (!isCartAdd) return;

        // Use current inputs if present; otherwise use saved props from Apply
        const anyText = this.inputs.some(o => (o.input.value || '').trim().length > 0);
        const props = anyText ? this._collectEngravingProps() : this.savedProps;
        if (props && Object.keys(props).length) this._writeHiddenPropsToForm(form, props);
      };

      document.addEventListener('click', this._onClick, true); // capture (run before theme handlers)
    }

    /* UPDATED: robust observer that keeps props on all forms after re-renders */
    _installFormObserver() {
      if (this._mo) this._mo.disconnect();

      this._mo = new MutationObserver(() => {
        if (!this.savedProps) return;
        // Ensure every current /cart/add form contains our hidden props
        document.querySelectorAll('form[action="/cart/add"]').forEach(f => {
          const flag = f.querySelector('input[name="properties[_engraving_applied]"]');
          if (!flag) this._writeHiddenPropsToForm(f, this.savedProps);
        });
      });

      // Observe the whole document — themes often re-render sticky bars/sections
      this._mo.observe(document.body, { childList: true, subtree: true });
    }

    clear() {
      this.inputs.forEach(o => {
        o.input.value = '';
        o.error.textContent = '';
        o.input.removeAttribute('aria-invalid');
        o.counter.textContent = (this.cfg.i18n.chars || '{{current}}/{{total}}')
          .replace('{{current}}','0').replace('{{total}}',String(this.cfg.maxCharsPerLine));
      });
      this._updatePreview();

      // Remove from whichever form is active
      const possibleForms = qsa(document, 'form[action="/cart/add"]');
      possibleForms.forEach(form => {
        qsa(form,
          'input[name^="properties[Гравиране – "],' +
          'input[name="properties[_engraving_payload_json]"],' +
          'input[name="properties[_engraving_applied]"],' +
          'input[name="properties[Engraving – Font]"]'
        ).forEach(n => n.remove());
      });

      this.savedProps = null;
      if (this.triggerLabel) this.triggerLabel.textContent = this.cfg.i18n.add || 'Гравирай';
      this.trigger?.classList.remove('is-engraved');

      this._announce(this.cfg.i18n.clear || 'Изчистено');
      this.root.dispatchEvent(new Event(EVENTS.CLEARED, { bubbles: true }));
    }

    _collectEngravingProps() {
  const props = {};
  const fontName = this.currentFont?.label || this.currentFont?.family || '';
  if (fontName) {
    // Keep ONLY the BG label in cart properties
    props['Гравиране – шрифт'] = fontName;
  }
  this.inputs.forEach((o, idx) => {
    const val = (o.input.value || '').trim();
    if (val) props[`Гравиране – ред ${idx+1}`] = val;
  });
  return props;
}


    async apply() {
      // Validate
      const hasAny = this.inputs.some(o => (o.input.value || '').trim().length > 0);
      if (!hasAny) {
        this._announce(this.cfg.i18n.errors?.empty || 'Моля, въведи текст за гравиране.');
        this.inputs[0]?.input?.focus();
        return;
      }
      const invalid = this.inputs.find(o => o.input.getAttribute('aria-invalid') === 'true');
      if (invalid) { invalid.input.focus(); return; }

      // Save engraving props to ALL /cart/add forms now; guards will keep them fresh later
      const engravingProps = this._collectEngravingProps();
      this._writeHiddenPropsToAllForms(engravingProps);
      this.savedProps = engravingProps;

      // UI feedback
      if (this.triggerLabel) {
        this.triggerLabel.textContent = 'Гравирането е добавено успешно, добави продукта в количка.';
      }
      this.trigger?.classList.add('is-engraved');

      this._announce('Гравирането е запазено. Добави продукта в количката.');
      this.root.dispatchEvent(new CustomEvent(EVENTS.APPLIED, { detail: { properties: engravingProps }, bubbles: true }));

      // Close drawer so the add-to-cart is visible (no auto-add)
      this.close();
    }

    _announce(msg) {
      if (!this.ariaLive) return;
      this.ariaLive.textContent = '';
      setTimeout(() => { this.ariaLive.textContent = msg; }, 10);
    }
  }

  const initAll = () => qsa(document, `[${SECTION_ATTR}]`).forEach(root => {
    if (root.__emEngraving) return;
    root.__emEngraving = new Engraving(root);
  });

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();

/* ===== EM Engraving – Font Tile Renderer ===== */
(function () {
  function buildFontTiles(root) {
    try {
      const cfgAttr = root.getAttribute('data-config') || '{}';
      const cfg = JSON.parse(cfgAttr);
      const fonts = Array.isArray(cfg.fonts) ? cfg.fonts : [];
      const host = root.querySelector('[data-engraving-fonts]');
      const labelEl = root.querySelector('[data-engraving-fontlabel]');
      const svgText = root.querySelector('[data-engraving-svg-text]');
      if (!host || fonts.length === 0) return;

      host.innerHTML = '';

      fonts.forEach((f, idx) => {
        const id = `em-font-${(root.id || 'x')}-${idx}`;
        const family = f.family || f.label || 'sans-serif';
        const fallback = f.fallback || 'system-ui, -apple-system, Segoe UI, Arial, sans-serif';
        const weight = f.weight || '400';
        const label = f.label || family;

        const card = document.createElement('label');
        card.className = 'em-engr__fontbox';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'em_engr_font';
        input.id = id;
        input.value = label;
        input.setAttribute('data-engraving-form-el', 'font-input');
        if (idx === 0) input.checked = true;

        const sample = document.createElement('span');
        sample.className = 'em-engr__fontsample';
        sample.textContent = 'Аа';
        sample.style.fontFamily = `${family}, ${fallback}`;
        sample.style.fontWeight = weight;

        const name = document.createElement('span');
        name.className = 'em-engr__fontname';
        name.textContent = label;

        card.append(input, sample, name);
        host.appendChild(card);

        input.addEventListener('change', () => {
          if (svgText) {
            svgText.style.fontFamily = `${family}, ${fallback}`;
            svgText.style.fontWeight = weight;
          }
          if (labelEl) labelEl.textContent = `Шрифт: ${label}`;
          root.dispatchEvent(new CustomEvent('engraving:fontchange', { detail: f }));
        });

        if (idx === 0) input.dispatchEvent(new Event('change'));
      });
    } catch (err) {
      console.warn('[EM Engraving] Font tile build failed:', err);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-engraving-root]').forEach(buildFontTiles);
  });
})();

/* ===== EM Engraving – Font Tile Renderer (runs on drawer open) ===== */
(function () {
  function buildFontTiles(root) {
    if (!root) return;
    let cfg = {};
    try { cfg = JSON.parse(root.getAttribute('data-config') || '{}'); }
    catch (e) { console.warn('[EM Engraving] bad config', e); return; }

    const fonts = Array.isArray(cfg.fonts) ? cfg.fonts : [];
    const host = root.querySelector('[data-engraving-fonts]');
    const labelEl = root.querySelector('[data-engraving-fontlabel]');
    const svgText = root.querySelector('[data-engraving-svg-text]');
    if (!host || !fonts.length) return;
    if (host.firstElementChild && host.firstElementChild.classList.contains('em-engr__fontbox')) return;

    host.innerHTML = '';

    fonts.forEach((f, idx) => {
      const id = `em-font-${(root.id || 'x')}-${idx}`;
      const family = f.family || f.label || 'sans-serif';
      const fallback = f.fallback || 'system-ui, -apple-system, Segoe UI, Arial, sans-serif';
      const weight = f.weight || '400';
      const label = f.label || family;

      const card = document.createElement('label');
      card.className = 'em-engr__fontbox';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'em_engr_font';
      input.id = id;
      input.value = label;
      if (idx === 0) input.checked = true;

      const sample = document.createElement('span');
      sample.className = 'em-engr__fontsample';
      sample.textContent = 'Аа';
      sample.style.fontFamily = `${family}, ${fallback}`;
      sample.style.fontWeight = weight;

      const name = document.createElement('span');
      name.className = 'em-engr__fontname';
      name.textContent = label;

      card.append(input, sample, name);
      host.appendChild(card);

      input.addEventListener('change', () => {
        if (svgText) {
          svgText.style.fontFamily = `${family}, ${fallback}`;
          svgText.style.fontWeight = weight;
        }
        if (labelEl) labelEl.textContent = `Шрифт: ${label}`;
        root.dispatchEvent(new CustomEvent('engraving:fontchange', { detail: f }));
      });

      if (idx === 0) input.dispatchEvent(new Event('change'));
    });
  }

  document.addEventListener('click', function (ev) {
    const btn = ev.target.closest('[data-engraving-trigger]');
    if (!btn) return;
    const root = btn.closest('[data-engraving-root]');
    setTimeout(() => buildFontTiles(root), 0);
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-engraving-root]').forEach(buildFontTiles);
  });
})();
